import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Politique de Confidentialité - Agent IA Pro | Protection des Données RGPD';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('robots', 'noindex, nofollow');
    setMeta('description', 'Politique de confidentialité d\'Agent IA Pro. Comment nous collectons, utilisons et protégeons vos données personnelles conformément au RGPD.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/privacy-policy`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/privacy-policy`);
      document.head.appendChild(canonical);
    }

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(s => s.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Politique de Confidentialité - Agent IA Pro",
      "description": "Politique de confidentialité d'Agent IA Pro. Protection des données personnelles conformément au RGPD.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/privacy-policy`,
      "publisher": {
        "@type": "Organization",
        "name": "Agent IA Pro",
        "url": import.meta.env.VITE_SITE_URL || 'https://example.com'
      }
    });
    document.head.appendChild(script);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Politique de Confidentialité
          </h1>
          
          <p className="text-gray-600 mb-12">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bienvenue sur notre plateforme d'agents IA. Nous accordons une grande importance à la protection de vos données personnelles et nous nous engageons à respecter votre vie privée. Cette politique de confidentialité explique comment nous collectons, utilisons, partageons et protégeons vos informations personnelles.
              </p>
              <p className="text-gray-700 leading-relaxed">
                En utilisant nos services, vous acceptez les pratiques décrites dans cette politique. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre plateforme.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                2. Informations que nous collectons
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    2.1 Informations que vous nous fournissez
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Nous collectons les informations que vous nous fournissez directement, notamment :
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>Nom, prénom et coordonnées (adresse e-mail, numéro de téléphone)</li>
                    <li>Informations de compte et d'authentification</li>
                    <li>Informations de paiement et de facturation</li>
                    <li>Communications avec notre support client</li>
                    <li>Préférences et paramètres de votre compte</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    2.2 Informations collectées automatiquement
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Lorsque vous utilisez notre plateforme, nous collectons automatiquement certaines informations :
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>Données de navigation (pages visitées, durée des sessions)</li>
                    <li>Adresse IP et informations sur votre appareil</li>
                    <li>Type de navigateur et système d'exploitation</li>
                    <li>Cookies et technologies similaires</li>
                    <li>Données d'utilisation des agents IA</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                3. Utilisation de vos informations
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous utilisons vos informations personnelles pour :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Fournir, maintenir et améliorer nos services d'agents IA</li>
                <li>Personnaliser votre expérience utilisateur</li>
                <li>Traiter vos paiements et gérer votre abonnement</li>
                <li>Vous envoyer des notifications importantes concernant votre compte</li>
                <li>Répondre à vos demandes et fournir un support client</li>
                <li>Analyser l'utilisation de notre plateforme pour améliorer nos services</li>
                <li>Détecter et prévenir les fraudes et abus</li>
                <li>Respecter nos obligations légales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                4. Partage de vos informations
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations uniquement dans les cas suivants :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Prestataires de services :</strong> Nous travaillons avec des partenaires de confiance qui nous aident à fournir nos services (hébergement, paiement, analyse)</li>
                <li><strong>Obligations légales :</strong> Si la loi l'exige ou pour protéger nos droits</li>
                <li><strong>Transfert d'entreprise :</strong> En cas de fusion, acquisition ou vente d'actifs</li>
                <li><strong>Avec votre consentement :</strong> Lorsque vous nous autorisez explicitement à partager vos données</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                5. Sécurité des données
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, modification, divulgation ou destruction. Ces mesures incluent :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Chiffrement des données en transit et au repos</li>
                <li>Contrôles d'accès stricts et authentification multi-facteurs</li>
                <li>Surveillance continue de nos systèmes</li>
                <li>Audits de sécurité réguliers</li>
                <li>Formation de notre personnel sur la protection des données</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                6. Vos droits
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Conformément au RGPD et aux lois applicables, vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification :</strong> Corriger vos données inexactes ou incomplètes</li>
                <li><strong>Droit à l'effacement :</strong> Demander la suppression de vos données</li>
                <li><strong>Droit à la limitation :</strong> Restreindre le traitement de vos données</li>
                <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données</li>
                <li><strong>Droit de retirer votre consentement :</strong> À tout moment</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Pour exercer ces droits, contactez-nous à l'adresse : <a href="mailto:privacy@example.com" className="text-teal-600 hover:text-teal-700 font-medium">privacy@example.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                7. Cookies et technologies similaires
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience sur notre plateforme. Les cookies nous permettent de :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Mémoriser vos préférences et paramètres</li>
                <li>Analyser le trafic et l'utilisation de notre site</li>
                <li>Personnaliser le contenu et les publicités</li>
                <li>Assurer la sécurité de votre compte</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                8. Conservation des données
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos services et respecter nos obligations légales. Lorsque vous supprimez votre compte, nous supprimons ou anonymisons vos données dans un délai raisonnable, sauf si nous devons les conserver pour des raisons légales ou réglementaires.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                9. Transferts internationaux
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Vos données peuvent être transférées et traitées dans des pays autres que votre pays de résidence. Nous nous assurons que ces transferts sont effectués conformément aux lois applicables et que vos données bénéficient d'un niveau de protection adéquat.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                10. Modifications de cette politique
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Nous pouvons mettre à jour cette politique de confidentialité de temps à autre. Nous vous informerons de tout changement important en publiant la nouvelle politique sur cette page et en mettant à jour la date de "Dernière mise à jour". Nous vous encourageons à consulter régulièrement cette page.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                11. Contact
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Pour toute question concernant cette politique de confidentialité ou le traitement de vos données personnelles, vous pouvez nous contacter :
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Email :</strong> <a href="mailto:privacy@example.com" className="text-teal-600 hover:text-teal-700">privacy@example.com</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Téléphone :</strong> +33 1 23 45 67 89
                </p>
                <p className="text-gray-700">
                  <strong>Adresse :</strong> 123 Avenue des Champs-Élysées, 75008 Paris, France
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
